/**
 * Device-local “current handwriting draft” pointer via `@capacitor/preferences` (persists on native;
 * on web the plugin uses localStorage under the hood).
 *
 * **Product shape:** Custom stationery is often reached in the **same tab** from this app, via a
 * **separate in-house storefront** (different deploy/repo until it ships inside this app). The
 * pointer lives in localStorage so that round trip does not require the server. Capacitor today may
 * use an in-app `/browse` iframe; web may use a full navigation away and back — same tab keeps this
 * origin’s `sessionStorage` bootstrap flag, so the pointer is usually **not** wiped on return.
 *
 * Cleared when leaving **this app’s** journey routes (router plugin) and once per **new browser tab**
 * (sessionStorage bootstrap — avoids stale ids from a previous tab session).
 */

import { Preferences } from '@capacitor/preferences'

/** Capacitor Preferences key (same key used on web via the plugin’s localStorage backend). */
export const HANDWRITING_JOURNEY_PREFERENCES_KEY = 'express_in_progress_handwriting_project_id'

/**
 * Marks that this **browser tab** has already run first-load cleanup for this origin.
 *
 * **Why:** `Preferences` uses localStorage on web, which can outlive the tab. We clear the pointer
 * the first time this app boots in a **new tab** so an old id is not reused. **Same tab:** leaving
 * for the external storefront and returning does not reset this flag (per-origin sessionStorage), so
 * the pointer typically remains for the in-house round trip.
 */
export const HANDWRITING_POINTER_SESSION_STORAGE_KEY = 'express.handwriting.pointer_session_v1'

/**
 * Whether `path` is still inside the **handwriting customize detour** (allowlist).
 *
 * **Why:** `handwriting-in-progress-pointer.client.ts` runs after each route change. If the user
 * navigates **from** a journey route **to** a non-journey route, we clear the Preferences pointer so
 * a stale `handwriting_project` id is not reused after they’ve left the flow (e.g. home, contacts).
 *
 * **Prefixes (this app’s router only):** `/handwritten-note/` (send/personalize), `/browse` (Capacitor
 * iframe host when used), `/designer/` (custom stationery). Time spent on a **separate storefront
 * app** does not hit Vue Router here — the pointer stays in localStorage until you return or we clear
 * it. Any **other** route inside this app counts as **outside** the journey (e.g. home).
 *
 * @param path - `to.path` / `from.path` from Vue Router (e.g. `/handwritten-note/personalize/…`)
 */
export function isHandwritingJourneyPath(path: string): boolean {
    if (!path) {
        return false
    }
    return (
        path.startsWith('/handwritten-note/') ||
        path.startsWith('/browse') ||
        path.startsWith('/designer/')
    )
}

/**
 * Read the stored handwriting project id (e.g. designer success → personalize resume).
 */
export async function getHandwritingJourneyProjectId(): Promise<string | null> {
    const { value } = await Preferences.get({ key: HANDWRITING_JOURNEY_PREFERENCES_KEY })
    return value ?? null
}

/**
 * Removes the pointer. Safe to call repeatedly (e.g. guests, boot cleanup).
 */
export async function clearProfileInProgressHandwritingSilent(): Promise<void> {
    try {
        await Preferences.remove({ key: HANDWRITING_JOURNEY_PREFERENCES_KEY })
    } catch {
        /* Rare plugin / storage failures */
    }
}

/**
 * Persists before opening customize (iframe/designer). Works without sign-in (unlike the old PATCH API).
 */
export async function setProfileInProgressHandwriting(projectId: string): Promise<void> {
    await Preferences.set({
        key: HANDWRITING_JOURNEY_PREFERENCES_KEY,
        value: projectId,
    })
}
